import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/uiDir/avatar";
import { Badge } from "@/components/uiDir/badge";
import { 
  Eye, 
  Heart, 
  Download, 
  UserPlus, 
  MessageSquare, 
  Share2,
  Loader2,
  Bell,
  ExternalLink
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Activity } from "@/lib/models/activityfeedModel";

export const ActivityFeed = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      fetchActivities();
      const subscription = supabase
        .channel('activities_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'activities',
            filter: `user_id=eq.${currentUserId}`
          },
          (payload) => {
            console.log('Activity change:', payload);
            fetchActivities();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [currentUserId]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchActivities = async () => {
    if (!currentUserId) return;

    try {
      setIsLoading(true);

      // Fetch activities with actor information
      const { data, error } = await supabase
        .from('activities')
        .select(`
          id,
          type,
          title,
          description,
          metadata,
          action_url,
          is_read,
          created_at,
          actor:actor_id (
            id,
            full_name,
            username,
            avatar_url
          )
        `)
        .eq('user_id', currentUserId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

       setActivities((data ?? []) as unknown as Activity[]);

      // Count unread activities
      const unread = (data || []).filter(a => !a.is_read).length;
      setUnreadCount(unread);

    } catch (err) {
      console.error('Error fetching activities:', err);
      toast.error('Failed to load activities');
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (activityId: string) => {
    try {
      const { error } = await supabase
        .from('activities')
        .update({ is_read: true })
        .eq('id', activityId);

      if (error) throw error;

      // Update local state
      setActivities(prev =>
        prev.map(a => a.id === activityId ? { ...a, is_read: true } : a)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

    } catch (err) {
      console.error('Error marking activity as read:', err);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUserId) return;

    try {
      const { error } = await supabase
        .from('activities')
        .update({ is_read: true })
        .eq('user_id', currentUserId)
        .eq('is_read', false);

      if (error) throw error;

      setActivities(prev => prev.map(a => ({ ...a, is_read: true })));
      setUnreadCount(0);
      toast.success('All activities marked as read');

    } catch (err) {
      console.error('Error marking all as read:', err);
      toast.error('Failed to mark all as read');
    }
  };

  const getActivityIcon = (type: string) => {
   const icons: Record<string, React.ElementType> = {
      profile_view: Eye,
      project_like: Heart,
      comment_like: Heart,
      resume_download: Download,
      new_follower: UserPlus,
      new_comment: MessageSquare,
      comment_reply: MessageSquare,
      portfolio_share: Share2,
      project_share: Share2,
      mention: Bell,
    };
    return icons[type] || Bell;
  };

  const getActivityColor = (type: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      profile_view: { bg: 'bg-blue-100', text: 'text-blue-600' },
      project_like: { bg: 'bg-red-100', text: 'text-red-600' },
      comment_like: { bg: 'bg-pink-100', text: 'text-pink-600' },
      resume_download: { bg: 'bg-green-100', text: 'text-green-600' },
      new_follower: { bg: 'bg-purple-100', text: 'text-purple-600' },
      new_comment: { bg: 'bg-orange-100', text: 'text-orange-600' },
      comment_reply: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
      portfolio_share: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
      project_share: { bg: 'bg-teal-100', text: 'text-teal-600' },
      mention: { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    };
    return colors[type] || { bg: 'bg-gray-100', text: 'text-gray-600' };
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - activityTime.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return activityTime.toLocaleDateString();
  };

  const getActionHref = (actionUrl: string) => {
    if (!actionUrl) return "";
    if (actionUrl.startsWith("http://") || actionUrl.startsWith("https://")) {
      return actionUrl;
    }
    if (actionUrl.startsWith("/")) {
      if (
        actionUrl.startsWith("/profile") ||
        actionUrl.startsWith("/dashboard") ||
        actionUrl.startsWith("/explore") ||
        actionUrl.startsWith("/activities")
      ) {
        return actionUrl;
      }
      return `/profile${actionUrl}`;
    }
    return `/profile/${actionUrl}`;
  };

  const handleActivityClick = (activity: Activity) => {
    if (!activity.is_read) {
      markAsRead(activity.id);
    }
  };

  if (isLoading) {
    return (
      <Card className="shadow-lg border-0 rounded-2xl bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading activities...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentUserId) {
    return (
      <Card className="shadow-lg border-0 rounded-2xl bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-600">Sign in to see your activities</p>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card className="shadow-lg border-0 rounded-2xl bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-2">No activities yet</p>
          <p className="text-xs text-gray-500">Your recent activities will appear here</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border-0 rounded-2xl bg-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Recent Activity</CardTitle>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white text-xs">
                {unreadCount}
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Mark all read
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {activities.map((activity) => {
            const Icon = getActivityIcon(activity.type);
            const colors = getActivityColor(activity.type);
            const actorAvatar = activity.actor?.avatar_url || 
              `https://api.dicebear.com/7.x/avataaars/svg?seed=${activity.actor?.username || 'user'}`;

            return (
              <div
                key={activity.id}
                className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer group hover:scale-[1.01] ${
                  activity.is_read 
                    ? 'bg-gray-50 hover:bg-gray-100' 
                    : 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
                }`}
                onClick={() => handleActivityClick(activity)}
              >
                {activity.actor ? (
                  <Link to={`/profile/${activity.actor.username}`}>
                    <Avatar className="h-10 w-10 border-2 border-white shadow-sm flex-shrink-0">
                      <AvatarImage src={actorAvatar} />
                      <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-semibold">
                        {activity.actor.full_name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                ) : (
                  <Avatar className="h-10 w-10 border-2 border-white shadow-sm flex-shrink-0">
                    <AvatarFallback className="bg-gray-100 text-gray-700 text-xs">
                      <Bell className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`font-medium text-sm mb-1 group-hover:text-blue-600 transition-colors ${
                      !activity.is_read ? 'text-gray-900' : 'text-gray-700'
                    }`}>
                      {activity.title}
                    </p>
                    {!activity.is_read && (
                      <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-1"></div>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 line-clamp-2 mb-1">
                    {activity.description}
                  </p>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {getTimeAgo(activity.created_at)}
                    </span>
                    {activity.action_url && (
                      <a
                        href={getActionHref(activity.action_url)}
                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>

                <div className={`p-2 rounded-lg ${colors.bg} group-hover:scale-110 transition-transform flex-shrink-0`}>
                  <Icon className={`h-4 w-4 ${colors.text}`} />
                </div>
              </div>
            );
          })}
        </div>

        {activities.length >= 10 && (
          <Link to="/activities" className="block mt-4">
            <button className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 font-medium hover:bg-blue-50 rounded-lg transition-colors">
              View All Activities
            </button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};